import { Button, Input } from "@/shared";
import Header from "@/shared/components/ui/Header";
import MainLayout from "@/shared/components/ui/MainLayout";
import {
    Document,
    Font,
    Image,
    Page,
    pdf,
    Text,
    View
} from "@react-pdf/renderer";
import bwipjs from 'bwip-js';
import { saveAs } from "file-saver";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
Font.register({
    family: "Tajawal",
    src: "https://fonts.gstatic.com/s/tajawal/v10/Iura6YBj_oCad4k1rzaLCr5IlLA.ttf",
});


const PrintSerials = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const name = searchParams.get('name');
    const data = searchParams.get('data');
    const [serials, setSerials] = useState([]);

    const [dimensions, setDimensions] = useState(() => {
        const saved = localStorage.getItem('label_settings')
        return saved ?
            JSON.parse(saved) :
            {
                width: 40,
                height: 25,
                number: 1,
                displayName: true,
                fontSize: 10,
                codeSize: 15,
                displayQty: true,
                factor: 1.5
            }
    });

    useEffect(() => {
        const savedData = localStorage.getItem('temp_print_serials');
        if (savedData) {
            try {
                setSerials(JSON.parse(savedData));
            } catch (e) {
                console.error("Error restoring data", e);
            }
        }

        const handleMessage = (event: MessageEvent) => {
            if (event?.data && event?.data.type === 'SERIALS_DATA') {
                const receivedPayload: [] = event.data.payload;

                if (Array.isArray(receivedPayload)) {
                    setSerials([...receivedPayload]);

                    localStorage.setItem('temp_print_serials', JSON.stringify(receivedPayload));
                }
            }
        };

        window.addEventListener('message', handleMessage);

        if (window.opener) {
            window.opener.postMessage("READY_TO_PRINT", "*");
        }
        return () => window.removeEventListener('message', handleMessage);
    }, []);


    useEffect(() => {
        localStorage.setItem('label_settings', JSON.stringify(dimensions))
    }, [dimensions]);

    const handleChange = (e: any) => {
        const { id, value } = e.target;
        setDimensions((prev) => ({ ...prev, [id]: value }))
    }

    const generateSerialBase64 = (text: string) => {
        try {
            const canvas = document.createElement('canvas');
            const factor = Number(dimensions.factor) || 1;
            bwipjs.toCanvas(canvas, {
                bcid: 'code128',
                text: text,
                scale: 3 * factor,
                height: Number(dimensions.codeSize) * factor,
                includetext: false,
            });
            return canvas.toDataURL('image/png');
        } catch (error) {
            console.log(error);
            return null
        }
    }
    const exportPdf = async () => {
        const factor = Number(dimensions.factor) || 1;
        const printData = serials?.length > 0 && data
            ? serials
            : Array.from({ length: Number(dimensions.number) }).map(() => ({ isBlank: true, serial_string: 'SAMPLE' }));
        try {
            const blob = await pdf(
                <Document>
                    {
                        printData.map((item: any, index) => {
                            const serialCode = !item.isBlank ? generateSerialBase64(item.serial_string) : null;
                            const pageWidth = Number(dimensions.width) * factor;
                            const pageHeight = Number(dimensions.height) * factor;
                            const fontSize = Number(dimensions.fontSize) * factor;
                            const serialCodeHeight = Number(dimensions.codeSize) * factor;
                            return (
                                <Page key={index}
                                    size={{
                                        width: `${pageWidth}mm`,
                                        height: `${pageHeight}mm`
                                    }}
                                    style={{ padding: 5, fontFamily: "Tajawal", }}>
                                    <View style={{
                                        flex: 1,
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        border: '0.5pt solid #eee'
                                    }}>
                                        {dimensions.displayName && (
                                            <Text style={{ fontSize: fontSize, marginBottom: 2, fontWeight: 'bold', textAlign: 'center' }}>
                                                {decodeURIComponent(name || '')}
                                            </Text>
                                        )}
                                        {dimensions.displayQty && (
                                            <Text style={{
                                                fontSize: fontSize,
                                                marginTop: 1,
                                                fontWeight: 'bold',
                                                textAlign: 'center',
                                                textTransform: 'uppercase'
                                            }}>
                                                {item?.remaning_quantity}
                                            </Text>
                                        )}
                                        {serialCode && (
                                            <Image
                                                src={serialCode}
                                                style={{
                                                    width: '90%',
                                                    height: serialCodeHeight,
                                                    marginTop: 2
                                                }}
                                            />
                                        )}
                                        {!item.isBlank && (
                                            <Text style={{
                                                fontSize: fontSize * 0.9,
                                                marginTop: 1,
                                                fontWeight: 'bold',
                                                textAlign: 'center',
                                                width: '90%',
                                                letterSpacing: 2,
                                                textTransform: 'uppercase'
                                            }}>
                                                {item?.serial_string}
                                            </Text>
                                        )}
                                    </View>
                                </Page>
                            )
                        })
                    }
                </Document>
            ).toBlob();
            saveAs(blob, decodeURIComponent(name || ''));
        } catch (error) {
            console.log(error);
        }
    }

    return (
        <MainLayout>
            <Header title={t("Sticker Settings")} info="" />
            <main className="px-4 py-8 min-h-[calc(100vh-100px)] flex flex-col items-center justify-center">
                <div className="max-w-4xl mx-auto">

                    <div className="p-6 bg-white rounded-[1rem] shadow-xl shadow-slate-200/50">
                        <h2 className="text-lg font-semibold text-gray-800 border-b pb-2">
                            {t("Sticker Settings")}
                        </h2>


                        <div className="flex items-center gap-2">
                            <input
                                id="displayName"
                                type="checkbox"
                                checked={dimensions.displayName}
                                onChange={(e) => { setDimensions(prev => ({ ...prev, displayName: e.target.checked })) }}
                                className="w-4 h-4 text-blue-600"
                            />
                            <label htmlFor="displayName" className="text-sm font-medium text-gray-700">
                                {t("Show Item Name")}
                            </label>
                        </div>

                        <div className="flex items-center gap-2 mt-4">
                            <input
                                id="displayQty"
                                type="checkbox"
                                checked={dimensions.displayQty}
                                onChange={(e) => { setDimensions(prev => ({ ...prev, displayQty: e.target.checked })) }}
                                className="w-4 h-4 text-blue-600"
                            />
                            <label htmlFor="displayQty" className="text-sm font-medium text-gray-700">
                                {t("Show Qty")}
                            </label>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-gray-500">{t("Width (mm)")}</label>
                                <Input id="width" type="number" value={dimensions.width} onChange={handleChange} />
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-gray-500">{t("Height (mm)")}</label>
                                <Input id="height" type="number" value={dimensions.height} onChange={handleChange} />
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-gray-500">{t("Font Size")}</label>
                                <Input id="fontSize" type="number" value={dimensions.fontSize} onChange={handleChange} />
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-gray-500">{t("Barcode Height")}</label>
                                <Input id="codeSize" type="number" value={dimensions.codeSize} onChange={handleChange} />
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-gray-500">{t("Factor")}</label>
                                <Input id="factor" type="number" value={dimensions.factor} onChange={handleChange} />
                            </div>
                        </div>
                        <Button
                            onClick={exportPdf}
                            className="mt-4 w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 rounded-lg transition-all"
                        >
                            {t("Print Labels")}
                        </Button>
                    </div>

                </div>

            </main>
        </MainLayout>

    );
}

export default PrintSerials;

